import * as L from 'leaflet';
import { Autohide } from './lib/Autohide';
import { Slider } from './lib/Slider';
import { createElement } from './lib/utils/dom';
/**
 * The layers transparency control.
 */
export class ControlLayersTransparency extends L.Control {
    /**
     * Create a new layers transparency control.
     * @param args - The constructor arguments.
     */
    constructor(args) {
        var _a;
        super(Object.assign(Object.assign({}, ControlLayersTransparency.controlOptionsDefault), args.controlOptions));
        this.groupNames = args.groupNames;
        this.maap = args.maap;
        this.transparencyInitial =
            (_a = args.transparencyInitial) !== null && _a !== void 0 ? _a : ControlLayersTransparency.transparencyInitialDefault;
    }
    /**
     * The hook to execute when the control is added to the map.
     * @param map - The leaflet map instance.
     * @returns The control container element.
     */
    onAdd(map) {
        const elContainer = this.createElContainer();
        const elTitle = this.createElTitle();
        const slider = this.createSlider();
        elContainer.appendChild(elTitle);
        elContainer.appendChild(slider.getContainerElement());
        this.autohide = new Autohide({
            element: elContainer,
            leafletMap: map,
        });
        return elContainer;
    }
    /**
     * The hook to execute when the control is removed from the map.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onRemove() {
        var _a;
        (_a = this.autohide) === null || _a === void 0 ? void 0 : _a.destroy();
    }
    /**
     * Create the control container DOM element.
     * @returns The control container DOM element.
     */
    createElContainer() {
        return createElement({
            tagName: 'div',
            classNames: [
                ControlLayersTransparency.className,
                'maap_c-control',
                'maap_c-floating',
            ],
        });
    }
    /**
     * Creat the title DOM element.
     * @returns The title DOM element.
     */
    createElTitle() {
        const className = `${ControlLayersTransparency.className}__title`;
        const elTitle = createElement({
            tagName: 'strong',
            classNames: [className],
        });
        elTitle.innerHTML = this.maap.i18n.translate('Transparency');
        return elTitle;
    }
    /**
     * Create the transparency slider instance.
     * @returns The transparency slider instance.
     */
    createSlider() {
        // calculate number of ticks (add one for the zero valued tick)
        const nTicks = ControlLayersTransparency.transparencyMax -
            ControlLayersTransparency.transparencyMin +
            1;
        const tickRecords = Array.from({ length: nTicks }, (_x, index) => ({
            value: index,
            label: `${index}%`,
        }));
        const initialIndex = tickRecords.findIndex((x) => x.value === this.transparencyInitial);
        if (initialIndex === -1) {
            throw new Error(`Unknown tick value ${this.transparencyInitial}`);
        }
        return new Slider({
            tickRecords: tickRecords,
            initialTick: initialIndex,
            onStateChangedCb: (value) => {
                const opacity = 1 - value / 100;
                this.setLayersOpacity(opacity);
            },
            onPointerDownCb: () => {
                var _a;
                (_a = this.autohide) === null || _a === void 0 ? void 0 : _a.lock();
            },
            onPointerUpCb: () => {
                var _a;
                (_a = this.autohide) === null || _a === void 0 ? void 0 : _a.unlock();
            },
        });
    }
    /**
     * Set the layers' opacity.
     * @param opacity - The opacity to set.
     */
    setLayersOpacity(opacity) {
        this.groupNames.forEach((groupName) => {
            const layers = this.maap.layers.getLayersByGroup(groupName);
            layers.forEach((layer) => {
                if (layer instanceof L.GridLayer || layer instanceof L.ImageOverlay) {
                    layer.setOpacity(opacity);
                }
            });
        });
    }
}
/**
 * The default leaflet control options.
 */
ControlLayersTransparency.controlOptionsDefault = {
    position: 'topright',
};
/**
 * The minimum transparency.
 */
ControlLayersTransparency.transparencyMin = 0;
/**
 * The maximum transparency.
 */
ControlLayersTransparency.transparencyMax = 100;
/**
 * The default initial transparency.
 */
ControlLayersTransparency.transparencyInitialDefault = 0;
/**
 * The control's base CSS class.
 */
ControlLayersTransparency.className = 'maap_p-control-layers-transparency';
//# sourceMappingURL=ControlLayersTransparency.js.map